import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { GraduationCap, Plus, Users, Upload, Trash2, ArrowLeft, FileSpreadsheet, Loader2, AlertCircle, Edit2 } from 'lucide-react'
import FileImporter from '../components/FileImporter'
import { api } from '../services/api'
import { getCurrentSchoolYear } from '../utils/schoolYear'

interface Class {
  id: string
  name: string
  level: string
  year: string
  student_count: number
}

export function ClassesPage() {
  const navigate = useNavigate()

  const [classes, setClasses] = useState<Class[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showImporter, setShowImporter] = useState(false)
  const [selectedClass, setSelectedClass] = useState<Class | null>(null)
  const [editingClass, setEditingClass] = useState<Class | null>(null)
  const [newClass, setNewClass] = useState({ 
    name: '', 
    level: '', 
    year: getCurrentSchoolYear()
  })
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)

  const currentSchoolYear = getCurrentSchoolYear()

  // 🎯 Ordre hiérarchique des niveaux (du plus bas au plus haut)
  const levelOrder: Record<string, number> = {
    'CAP': 1,
    'Seconde': 2,
    'Première': 3,
    'Terminale': 4,
    'BTS': 5,
    'Autre': 99  // À la fin
  }

  // 🔄 Trier les classes par niveau puis par nom
  const sortedClasses = [...classes].sort((a, b) => {
    const orderA = levelOrder[a.level] || 50  // Si niveau inconnu, au milieu
    const orderB = levelOrder[b.level] || 50
    
    if (orderA !== orderB) {
      return orderA - orderB  // Tri par niveau
    }
    
    return a.name.localeCompare(b.name)  // Si même niveau, tri alphabétique
  })

  useEffect(() => {
    loadClasses()
  }, [])

  const loadClasses = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.getClasses()
      
      if (!Array.isArray(data)) {
        console.error('API returned non-array:', data)
        setClasses([])
        setError('Format de données invalide reçu du serveur')
        return
      }
      
      setClasses(data)
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des classes')
      console.error('Error loading classes:', err)
      setClasses([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreateClass = async () => {
    if (!newClass.name || !newClass.level) return
    
    try {
      setCreating(true)
      await api.createClass({
        name: newClass.name,
        level: newClass.level,
        year: newClass.year,
        description: ''
      })
      
      setNewClass({ 
        name: '', 
        level: '', 
        year: getCurrentSchoolYear()
      })
      setShowCreateForm(false)
      await loadClasses()
    } catch (err: any) {
      alert('Erreur lors de la création: ' + err.message)
    } finally {
      setCreating(false)
    }
  }

  // ✨ NOUVELLE FONCTION : Édition de classe
  const handleEditClick = (classItem: Class) => {
    setEditingClass(classItem)
    setShowEditForm(true)
  }

  const handleUpdateClass = async () => {
    if (!editingClass || !editingClass.name || !editingClass.level) return
    
    try {
      setUpdating(true)
      await api.updateClass(editingClass.id, {
        name: editingClass.name,
        level: editingClass.level,
        year: editingClass.year,
        description: ''
      })
      
      setEditingClass(null)
      setShowEditForm(false)
      await loadClasses()
    } catch (err: any) {
      alert('Erreur lors de la modification: ' + err.message)
    } finally {
      setUpdating(false)
    }
  }

  const handleDeleteClass = async (classId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette classe ?')) return
    
    try {
      await api.deleteClass(classId)
      await loadClasses()
    } catch (err: any) {
      alert('Erreur lors de la suppression: ' + err.message)
    }
  }

  const handleImportClick = (classItem: Class) => {
    setSelectedClass(classItem)
    setShowImporter(true)
  }

  const handleImportSuccess = () => {
    setShowImporter(false)
    loadClasses()
  }

  const totalStudents = Array.isArray(classes) 
    ? classes.reduce((sum, c) => sum + (parseInt(c.student_count as any) || 0), 0)
    : 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <GraduationCap className="w-8 h-8 text-blue-600" />
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Gestion des Classes</h1>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:shadow-lg transition-all hover:scale-105"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Nouvelle Classe</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Classes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{classes.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Élèves</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{totalStudents}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Année Scolaire</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{currentSchoolYear}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-900 font-semibold">Erreur</p>
              <p className="text-red-700 text-sm">{error}</p>
              <button
                onClick={loadClasses}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Réessayer
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="ml-3 text-gray-600">Chargement des classes...</p>
          </div>
        ) : (
          <>
            {/* Classes Grid */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Mes Classes</h2>
              
              {classes.length === 0 ? (
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-12 border border-gray-200 text-center">
                  <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Aucune classe créée</p>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="px-6 py-2 rounded-lg bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors"
                  >
                    Créer ma première classe
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* 🔄 Utilisation des classes triées */}
                  {sortedClasses.map((classItem) => (
                    <div
                      key={classItem.id}
                      className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all hover:scale-[1.02] group"
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{classItem.name}</h3>
                          <p className="text-sm text-gray-500">{classItem.level} - {classItem.year}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* ✨ NOUVEAU : Bouton éditer */}
                          <button
                            onClick={() => handleEditClick(classItem)}
                            className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClass(classItem.id)}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Nombre d'élèves</span>
                          <span className="text-2xl font-bold text-blue-600">
                            {classItem.student_count || 0}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="space-y-2">
                        <button
                          onClick={() => handleImportClick(classItem)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:shadow-lg transition-all"
                        >
                          <Upload className="w-4 h-4" />
                          Importer Élèves
                        </button>
                        
                        <button
                          disabled
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-gray-200 text-gray-400 font-semibold cursor-not-allowed"
                        >
                          <Users className="w-4 h-4" />
                          Voir Élèves (bientôt)
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal Création Classe */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 animate-scale-in">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Nouvelle Classe</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la classe *
                </label>
                <input
                  type="text"
                  value={newClass.name}
                  onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                  placeholder="Ex: TBPRO1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Niveau *
                </label>
                <select
                  value={newClass.level}
                  onChange={(e) => setNewClass({ ...newClass, level: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionnez un niveau</option>
                  <option value="CAP">CAP</option>
                  <option value="Seconde">Seconde</option>
                  <option value="Première">Première</option>
                  <option value="Terminale">Terminale</option>
                  <option value="BTS">BTS</option>
                  <option value="Autre">Autre niveau</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Année scolaire
                </label>
                <input
                  type="text"
                  value={newClass.year}
                  onChange={(e) => setNewClass({ ...newClass, year: e.target.value })}
                  placeholder={currentSchoolYear}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreateClass}
                disabled={!newClass.name || !newClass.level || creating}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                {creating ? 'Création...' : 'Créer'}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                disabled={creating}
                className="flex-1 px-4 py-2 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✨ NOUVEAU : Modal Édition Classe */}
      {showEditForm && editingClass && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 animate-scale-in">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Éditer la Classe</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la classe *
                </label>
                <input
                  type="text"
                  value={editingClass.name}
                  onChange={(e) => setEditingClass({ ...editingClass, name: e.target.value })}
                  placeholder="Ex: TBPRO1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Niveau *
                </label>
                <select
                  value={editingClass.level}
                  onChange={(e) => setEditingClass({ ...editingClass, level: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionnez un niveau</option>
                  <option value="CAP">CAP</option>
                  <option value="Seconde">Seconde</option>
                  <option value="Première">Première</option>
                  <option value="Terminale">Terminale</option>
                  <option value="BTS">BTS</option>
                  <option value="Autre">Autre niveau</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Année scolaire
                </label>
                <input
                  type="text"
                  value={editingClass.year}
                  onChange={(e) => setEditingClass({ ...editingClass, year: e.target.value })}
                  placeholder={currentSchoolYear}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdateClass}
                disabled={!editingClass.name || !editingClass.level || updating}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {updating && <Loader2 className="w-4 h-4 animate-spin" />}
                {updating ? 'Modification...' : 'Enregistrer'}
              </button>
              <button
                onClick={() => {
                  setShowEditForm(false)
                  setEditingClass(null)
                }}
                disabled={updating}
                className="flex-1 px-4 py-2 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Import */}
      {showImporter && selectedClass && (
        <FileImporter
          classId={selectedClass.id}
          className={selectedClass.name}
          onClose={() => setShowImporter(false)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  )
}
